import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Alert, Skeleton } from '@mui/material';
import { horsesAPI, breedingAPI, appointmentsAPI, inventoryAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { formatDateTime } from '../utils/dateFormat';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import { toArray } from '../utils/apiData';
import { cardSx } from '../theme';
import { useSkeletonAnimation } from '../components/LoadingSkeletons';

// ── Design tokens ────────────────────────────────────────────────────────────
const T = {
  parchment:   '#F5F0E8',
  parchmentDk: '#EDE5D8',
  dark:        '#2C1810',
  dark700:     '#3D2318',
  dark500:     '#5C3420',
  gold:        '#C9922A',
  goldLight:   '#E8C070',
  cream:       '#FAF7F2',
  green:       '#4A7C59',
  greenLight:  '#D4EAD8',
  red:         '#8B3A2A',
  redLight:    '#F5DDD8',
  muted:       '#8A7060',
  border:      '#DDD0C0',
};

// ── Helpers ──────────────────────────────────────────────────────────────────
// Called per render, not at module load: a tab left open overnight re-renders on focus
// (useRefreshOnFocus) and must show the new day, not the day the bundle was parsed.
const describeToday = () =>
  new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

// ── Progress bar ─────────────────────────────────────────────────────────────
const Bar = ({ value, max, color = T.gold }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <Box sx={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.15)', mt: 1 }}>
      <Box sx={{ height: '100%', width: `${pct}%`, borderRadius: 3, backgroundColor: color, transition: 'width 0.6s ease' }} />
    </Box>
  );
};

const LightBar = ({ value, max, color = T.gold }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <Box sx={{ height: 6, borderRadius: 3, backgroundColor: T.parchmentDk, mt: 0.75, mb: 1.5 }}>
      <Box sx={{ height: '100%', width: `${pct}%`, borderRadius: 3, backgroundColor: color, transition: 'width 0.6s ease' }} />
    </Box>
  );
};

// Skeleton tinted light so it shows on the dark hero panel.
const HeroBone = ({ sx, ...props }) => (
  <Skeleton animation={useSkeletonAnimation()} sx={{ bgcolor: 'rgba(255,255,255,0.14)', ...sx }} {...props} />
);

// ── Hero stat panel ───────────────────────────────────────────────────────────
const HeroStat = ({ label, value, unit, sub, barValue, barMax, barColor, badge, loading }) => (
  <Box sx={{ flex: 1, flexBasis: { xs: '50%', md: 'auto' }, px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2 }, borderRight: `1px solid rgba(255,255,255,0.08)`, '&:last-child': { borderRight: 0 } }}>
    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.goldLight, mb: 1 }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
      <Typography sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 700, lineHeight: 1, color: '#fff' }}>
        {loading ? <HeroBone width="2ch" /> : value}
      </Typography>
      {unit && !loading && <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', mb: 0.5 }}>{unit}</Typography>}
    </Box>
    {/* While loading, a zero-width bar and "0% of herd" would read as real figures. */}
    {loading ? <HeroBone width="70%" sx={{ mt: 1 }} /> : (
      <>
        {barMax !== undefined && <Bar value={barValue ?? value} max={barMax} color={barColor} />}
        {sub && <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', mt: 0.75 }}>{sub}</Typography>}
      </>
    )}
    {badge && !loading && (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mt: 1, px: 1.25, py: 0.5, borderRadius: 20, border: `1px solid ${T.goldLight}`, backgroundColor: 'rgba(201,146,42,0.15)' }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: T.goldLight }} />
        <Typography sx={{ fontSize: '0.72rem', color: T.goldLight, fontWeight: 600 }}>{badge}</Typography>
      </Box>
    )}
  </Box>
);

// ── Composition row ───────────────────────────────────────────────────────────
const CompRow = ({ label, emoji, value, max, color }) => (
  <Box sx={{ mb: 0.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '1rem' }}>{emoji}</Typography>
        <Typography sx={{ fontSize: '0.875rem', color: T.dark, fontWeight: 500 }}>{label}</Typography>
      </Box>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: T.dark }}>{value}</Typography>
    </Box>
    <LightBar value={value} max={max} color={color} />
  </Box>
);

// ── Panel card ────────────────────────────────────────────────────────────────
const Panel = ({ title, action, actionLabel, children, loading }) => {
  const navigate = useNavigate();
  const animation = useSkeletonAnimation();
  return (
    <Box sx={{ flex: 1, backgroundColor: T.cream, border: `1px solid ${T.border}`, boxShadow: cardSx.boxShadow, borderRadius: 3, p: 2.5, display: 'flex', flexDirection: 'column', gap: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.dark }}>
          {title}
        </Typography>
        {actionLabel && (
          <Typography
            onClick={() => navigate(action)}
            sx={{ fontSize: '0.78rem', fontWeight: 600, color: T.gold, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {actionLabel} →
          </Typography>
        )}
      </Box>
      {/* While loading, "All clear" / "All stocked" would be claims about data not yet read. */}
      {loading
        ? ['80%', '60%', '70%'].map((w) => <Skeleton key={w} animation={animation} width={w} height={28} />)
        : children}
    </Box>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const [horses, setHorses] = useState({});
  const [breeding, setBreeding] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadDashboard = useCallback(async () => {
    setLoadError('');
    try {
      const [h, b, a, i] = await Promise.all([
        horsesAPI.getStats(),
        breedingAPI.getStats(),
        appointmentsAPI.getUpcomingVetAppointments(),
        inventoryAPI.getLowStock(),
      ]);
      setHorses(h?.data ?? {});
      setBreeding(b?.data ?? {});
      setAppointments(toArray(a?.data, 'upcoming appointments'));
      setLowStock(toArray(i?.data, 'low stock items'));
    } catch (e) {
      console.error(e);
      // The previous figures are left in place on purpose: a failed refresh should say
      // so rather than silently render every tile as a zero.
      setLoadError(e?.response?.data?.error || 'Unable to load dashboard figures. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useRefreshOnFocus(loadDashboard);

  const totalHorses  = horses.total_horses?.total ?? 0;
  const owned        = horses.total_horses?.by_owner_type?.owned ?? 0;
  const leased       = horses.total_horses?.by_owner_type?.leased ?? 0;
  const forSale      = horses.for_sale_count ?? 0;
  const totalMares   = horses.mares?.total ?? 0;
  const inFoal       = breeding.confirmed_in_foal?.total ?? 0;
  const curBreedings = breeding.current_season?.total ?? 0;
  const mares        = horses.mares?.total ?? 0;
  const currentHorses = horses.current_horses ?? 0;
  const notBred      = mares > 0 ? mares - inFoal : 0;
  const byType       = horses.current_horses_by_type ?? {};
  const dateStr = describeToday();

  return (
    <Box sx={{ backgroundColor: T.parchment, minHeight: '100vh', p: { xs: 2, sm: 3 } }}>

      {loadError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      ) : null}

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 0 } }}>
        <Box>
          <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: T.dark, lineHeight: 1.1 }}>
            Dashboard
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', color: T.muted, mt: 0.5 }}>
            {dateStr}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<SmartToyIcon />}
            onClick={() => navigate('/assistant')}
            sx={{ borderColor: T.dark, color: T.dark, textTransform: 'none', fontWeight: 600, borderRadius: 2, '&:hover': { backgroundColor: T.parchmentDk }, boxShadow: 'none' }}
          >
            Ask Assistant
          </Button>
        </Box>
      </Box>

      {/* ── Hero dark panel ── */}
      <Box sx={{
        backgroundColor: T.dark,
        borderRadius: 3,
        mb: 3,
        overflow: 'hidden',
        display: 'flex',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
      }}>
        <HeroStat
          loading={loading}
          label="Herd Size"
          value={totalHorses}
          unit="horses"
          barValue={owned}
          barMax={totalHorses}
          barColor={T.gold}
          sub={`Owned ${owned}  ·  Leased ${leased}`}
        />
        <HeroStat
          loading={loading}
          label="For Sale"
          value={forSale}
          unit="horses"
          barValue={forSale}
          barMax={totalHorses || 1}
          barColor="#64B5F6"
          sub={totalHorses > 0 ? `${Math.round((forSale / totalHorses) * 100)}% of herd` : ''}
        />
        <HeroStat
          loading={loading}
          label="Active Mares"
          value={totalMares}
          unit={`of ${totalHorses}`}
          barValue={totalMares}
          barMax={totalHorses}
          barColor={T.goldLight}
          sub={totalHorses > 0 ? `${Math.round((totalMares / totalHorses) * 100)}% of herd active` : ''}
        />
        <HeroStat
          loading={loading}
          label="Confirmed In Foal"
          value={inFoal}
          unit="mares"
          barValue={inFoal}
          barMax={totalMares || 1}
          barColor="#6BBF8A"
          sub={totalMares > 0 ? `${Math.round((inFoal / totalMares) * 100)}% of active mares` : ''}
        />
        <HeroStat
          loading={loading}
          label="Season Breedings"
          value={curBreedings}
          unit="this season"
          badge={curBreedings === 0 ? 'Season open' : `${curBreedings} recorded`}
        />
      </Box>

      {/* ── Bottom 3-panel row ── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>

        {/* Herd Composition */}
        <Panel title="Herd Composition" loading={loading}>
          {Object.entries(byType).length > 0 ? (
            <>
              {byType.Mare    > 0 && <CompRow label="Mares"    emoji="🐎" value={byType.Mare}    max={currentHorses} color={T.gold} />}
              {byType.Stallion > 0 && <CompRow label="Stallions" emoji="🐴" value={byType.Stallion} max={currentHorses} color={T.red} />}
              {byType.Gelding > 0 && <CompRow label="Geldings"  emoji="🐴" value={byType.Gelding}  max={currentHorses} color={T.muted} />}
              {byType.Colt    > 0 && <CompRow label="Colts"     emoji="🐴" value={byType.Colt}     max={currentHorses} color={T.green} />}
              {byType.Filly   > 0 && <CompRow label="Fillies"   emoji="🐎" value={byType.Filly}    max={currentHorses} color="#B07CC6" />}
            </>
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: T.muted }}>No current horses</Typography>
          )}

          {/* In Foal / Not Bred mini cards */}
          {mares > 0 && (
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
              <Box sx={{ flex: 1, backgroundColor: T.parchmentDk, borderRadius: 2, p: 1.5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: T.dark }}>{inFoal}</Typography>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.muted }}>In Foal</Typography>
              </Box>
              <Box sx={{ flex: 1, backgroundColor: T.parchmentDk, borderRadius: 2, p: 1.5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: T.dark }}>{notBred}</Typography>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.muted }}>Not Bred</Typography>
              </Box>
            </Box>
          )}
        </Panel>

        {/* Upcoming Appointments */}
        <Panel title="Upcoming Appointments" action="/appointments/new" actionLabel="+ Schedule" loading={loading}>
          {appointments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CalendarMonthIcon sx={{ fontSize: 40, color: T.border, mb: 1 }} />
              <Typography sx={{ fontWeight: 600, color: T.dark, mb: 0.5 }}>All clear</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: T.muted, mb: 2 }}>No upcoming appointments scheduled</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/appointments/new')}
                sx={{ borderColor: T.dark, color: T.dark, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                Schedule Vet Visit
              </Button>
            </Box>
          ) : (
            appointments.slice(0, 5).map((a) => (
              <Box key={a.id} sx={{ py: 1.25, borderBottom: `1px solid ${T.border}`, '&:last-child': { borderBottom: 0 } }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: T.dark }}>{a.horse_name} — {a.appointment_type}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: T.muted }}>{formatDateTime(a.appointment_date)}</Typography>
              </Box>
            ))
          )}

          {/* Farrier / Vet quick status */}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
            {[{ label: 'Farrier', value: 'None due' }, { label: 'Vet Check', value: 'None due' }].map(({ label, value }) => (
              <Box key={label} sx={{ flex: 1, backgroundColor: T.parchmentDk, borderRadius: 2, p: 1.25, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.68rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>{label}</Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: T.dark }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        </Panel>

        {/* Inventory Status */}
        <Panel title="Inventory Status" action="/inventory" actionLabel="View all" loading={loading}>
          {lowStock.length === 0 ? (
            <Box sx={{ backgroundColor: T.greenLight, borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <CheckCircleIcon sx={{ color: T.green, fontSize: 20 }} />
              <Box>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: T.green }}>All stocked</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: T.green, opacity: 0.8 }}>No low stock alerts</Typography>
              </Box>
            </Box>
          ) : (
            lowStock.slice(0, 3).map((item) => (
              <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: `1px solid ${T.border}` }}>
                <Typography sx={{ fontSize: '0.875rem', color: T.dark }}>{item.item_name}</Typography>
                <Box sx={{ px: 1, py: 0.25, backgroundColor: T.redLight, borderRadius: 1 }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: T.red }}>Low</Typography>
                </Box>
              </Box>
            ))
          )}

          {/* Category rows */}
          {[{ label: 'Feed', emoji: '🌾' }, { label: 'Medications', emoji: '💊' }, { label: 'Supplies', emoji: '🧴' }].map(({ label, emoji }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.25, borderBottom: `1px solid ${T.parchmentDk}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '1rem' }}>{emoji}</Typography>
                <Typography sx={{ fontSize: '0.875rem', color: T.dark }}>{label}</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: T.green }}>OK</Typography>
            </Box>
          ))}
        </Panel>

      </Box>
    </Box>
  );
};

export default Dashboard;